import type { PhotoListResponse, PhotoSummary, PhotoVariantSummary } from '@shared/api';
import type {
  NewShot,
  NewVariant,
  PhotoStorage,
  ShotImage,
  StoredShot,
} from '@/capture/PhotoStorage';
import type { RenderQualityTier } from '@/capture/ImageSpec';
import type { SceneManifest } from '@/capture/SceneManifest';
import type { CameraSnapshot } from '@/capture/CaptureService';
import type { EnvironmentState } from '@/environment/EnvironmentState';
import { ApiClient, CloudError } from './ApiClient';
import type { CloudSession } from './Session';

/**
 * The photo gallery backed by the account instead of IndexedDB.
 *
 * A shot is four PNGs plus a scene manifest, a camera snapshot, and the environment state. The
 * split across the wire follows what each part is for: the JSON is small and read on every gallery
 * open, the images are large and read once.
 *
 * This is where {@link ShotImage} earns itself. Every image is addressed by an API URL that the
 * browser can put straight into an `<img src>` — the session cookie authenticates it, so the
 * gallery renders from one JSON request and no image transfer at all. Bytes are pulled only by the
 * two callers that need them: the enhance panel, re-sending a capture to the model, and a download
 * link. Those go through a promise cache, so re-rendering a card does not re-download anything.
 *
 * The alternative — download four images per shot before the gallery can paint — would mean a page
 * that hangs for a minute on a gallery of forty shots, most of which the user will only ever see as
 * a thumbnail.
 */

export interface CloudPhotoStoreOptions {
  session: CloudSession;
  api?: ApiClient;
}

export class CloudPhotoStore implements PhotoStorage {
  private readonly api: ApiClient;
  private readonly session: CloudSession;
  private sessionLost = false;
  /** Memoised image fetches, keyed by URL, so re-reading a shot does not re-download it. */
  private readonly blobCache = new Map<string, Promise<Blob>>();

  constructor(options: CloudPhotoStoreOptions) {
    this.session = options.session;
    this.api = options.api ?? options.session.api;
  }

  get persistent(): boolean {
    return !this.sessionLost;
  }

  async save(shot: NewShot): Promise<StoredShot> {
    const form = new FormData();
    form.append(
      'meta',
      JSON.stringify({
        title: shot.title,
        size: shot.size,
        manifest: shot.manifest,
        camera: shot.camera,
        environment: shot.environment,
      }),
    );
    form.append('beauty', shot.beauty, 'beauty.png');
    form.append('idMap', shot.idMap, 'id-map.png');
    form.append('depth', shot.depth, 'depth.png');
    form.append('thumbnail', shot.thumbnail, 'thumbnail.png');

    const response = await this.api.postForm<{ photo: PhotoSummary }>('/api/photos', form);

    // The blobs are already in hand, so seed the cache rather than making the very next read
    // download what was just uploaded.
    this.blobCache.set(response.photo.images.beauty, Promise.resolve(shot.beauty));
    this.blobCache.set(response.photo.images.idMap, Promise.resolve(shot.idMap));
    this.blobCache.set(response.photo.images.depth, Promise.resolve(shot.depth));
    this.blobCache.set(response.photo.images.thumbnail, Promise.resolve(shot.thumbnail));

    return this.hydrate(response.photo);
  }

  async list(): Promise<StoredShot[]> {
    const response = await this.guard(() => this.api.getJson<PhotoListResponse>('/api/photos'));
    if (response === null) return [];
    return response.photos.map((photo) => this.hydrate(photo));
  }

  async get(id: string): Promise<StoredShot | undefined> {
    const shots = await this.list();
    return shots.find((shot) => shot.id === id);
  }

  async addVariant(shotId: string, variant: NewVariant): Promise<StoredShot | undefined> {
    const form = new FormData();
    form.append(
      'meta',
      JSON.stringify({
        presetId: variant.presetId,
        presetLabel: variant.presetLabel,
        prompt: variant.prompt,
        model: variant.model,
        quality: variant.quality,
        size: variant.size,
        estimatedCostUsd: variant.estimatedCostUsd,
      }),
    );
    form.append('image', variant.image, 'variant.png');

    try {
      const { variant: created } = await this.api.postForm<{ variant: PhotoVariantSummary }>(
        `/api/photos/${encodeURIComponent(shotId)}/variants`,
        form,
      );
      this.blobCache.set(created.imageUrl, Promise.resolve(variant.image));
    } catch (error) {
      if (error instanceof CloudError && error.code === 'not_found') return undefined;
      if (error instanceof CloudError) this.noteSessionLoss(error);
      throw error;
    }
    return this.get(shotId);
  }

  async removeVariant(shotId: string, variantId: string): Promise<StoredShot | undefined> {
    await this.guard(() =>
      this.api.delete(
        `/api/photos/${encodeURIComponent(shotId)}/variants/${encodeURIComponent(variantId)}`,
      ),
    );
    return this.get(shotId);
  }

  async rename(shotId: string, title: string): Promise<StoredShot | undefined> {
    try {
      const { photo } = await this.api.patchJson<{ photo: PhotoSummary }>(
        `/api/photos/${encodeURIComponent(shotId)}`,
        { title },
      );
      return this.hydrate(photo);
    } catch (error) {
      if (error instanceof CloudError && error.code === 'not_found') return undefined;
      if (error instanceof CloudError) this.noteSessionLoss(error);
      throw error;
    }
  }

  async remove(id: string): Promise<void> {
    await this.guard(() => this.api.delete(`/api/photos/${encodeURIComponent(id)}`));
  }

  async clear(): Promise<void> {
    const response = await this.guard(() => this.api.getJson<PhotoListResponse>('/api/photos'));
    if (response === null) return;
    // Sequential rather than parallel: this is a destructive bulk action against a rate-limited
    // API, and a burst of forty deletes is exactly the shape that trips the limiter and leaves the
    // gallery half-cleared.
    for (const photo of response.photos) {
      await this.api.delete(`/api/photos/${encodeURIComponent(photo.id)}`);
    }
    this.blobCache.clear();
  }

  async usageBytes(): Promise<number> {
    const response = await this.guard(() => this.api.getJson<PhotoListResponse>('/api/photos'));
    // Counted server-side from what is actually stored, rather than by summing blob sizes the way
    // the local store does — which would mean downloading the gallery to answer a status readout.
    return response?.usageBytes ?? 0;
  }

  close(): void {
    this.blobCache.clear();
  }

  /** Turn a server summary into the record the app expects. */
  private hydrate(photo: PhotoSummary): StoredShot {
    return {
      id: photo.id,
      createdAt: photo.createdAt,
      title: photo.title,
      size: photo.size,
      manifest: photo.manifest as SceneManifest,
      camera: photo.camera as CameraSnapshot,
      environment: photo.environment as EnvironmentState,
      beauty: this.image(photo.images.beauty),
      idMap: this.image(photo.images.idMap),
      depth: this.image(photo.images.depth),
      thumbnail: this.image(photo.images.thumbnail),
      variants: photo.variants.map((variant) => ({
        id: variant.id,
        createdAt: variant.createdAt,
        presetId: variant.presetId,
        presetLabel: variant.presetLabel,
        prompt: variant.prompt,
        model: variant.model,
        quality: variant.quality as RenderQualityTier,
        size: variant.size,
        estimatedCostUsd: variant.estimatedCostUsd,
        image: this.image(variant.imageUrl),
      })),
    };
  }

  /**
   * A stored image, addressable without transferring it.
   *
   * `url` is the API path, resolved against the configured base — usable directly as an `<img src>`
   * because the browser sends the session cookie with it. `blob()` is the expensive half and is
   * memoised per URL.
   */
  private image(path: string): ShotImage {
    return {
      url: this.api.url(path),
      blob: () => this.fetchBlob(path),
    };
  }

  private fetchBlob(path: string): Promise<Blob> {
    const cached = this.blobCache.get(path);
    if (cached) return cached;

    const pending = this.api.getBlob(path).catch((error: unknown) => {
      // A failed image must not poison the cache — the next attempt should be able to retry.
      this.blobCache.delete(path);
      if (error instanceof CloudError) this.noteSessionLoss(error);
      throw error;
    });
    this.blobCache.set(path, pending);
    return pending;
  }

  private async guard<T>(call: () => Promise<T>): Promise<T | null> {
    try {
      const result = await call();
      this.sessionLost = false;
      return result;
    } catch (error) {
      if (error instanceof CloudError && error.needsSignIn) {
        this.noteSessionLoss(error);
        return null;
      }
      throw error;
    }
  }

  private noteSessionLoss(error: CloudError): void {
    if (!error.needsSignIn) return;
    this.sessionLost = true;
    this.session.markSignedOut();
  }
}
